import { useQuery } from "@tanstack/react-query";
import type {
  PlatformAdminRow,
  PlatformAuditPage,
  PlatformJobs,
  PlatformMember,
  PlatformOverview,
  PlatformPersonResult,
  PlatformSupportSession,
  PlatformTenantDetail,
  PlatformTenantList,
} from "@hik/contracts/platform";
import { get, qs } from "../../lib/api";

/**
 * 21ik Platform paneli istekleri (/api/v1/platform/*). Oturum ayrı çerezle (hik_platform) taşınır; panelin 401'i
 * firma oturumuna dokunmaz (lib/api.ts). Sorgu anahtarları "platform" önekiyle firma önbelleğinden ayrılır.
 */
export const pk = {
  session: ["platform", "session"] as const,
  overview: ["platform", "overview"] as const,
  tenants: (p: Record<string, unknown>) => ["platform", "tenants", p] as const,
  tenant: (id: string) => ["platform", "tenant", id] as const,
  members: (id: string, q: string) => ["platform", "members", id, q] as const,
  people: (q: string) => ["platform", "people", q] as const,
  jobs: (status: string) => ["platform", "jobs", status] as const,
  audit: (action: string) => ["platform", "audit", action] as const,
  admins: ["platform", "admins"] as const,
};

export const useOverview = () => useQuery({ queryKey: pk.overview, queryFn: () => get<PlatformOverview>("/platform/overview") });

export const useTenants = (p: { q?: string; status?: string; sort?: string; offset?: number }) =>
  useQuery({
    queryKey: pk.tenants(p),
    queryFn: () => get<PlatformTenantList>(`/platform/tenants${qs({ ...p, limit: 50 })}`),
    placeholderData: (prev) => prev,
  });

export const useTenant = (id: string) => useQuery({ queryKey: pk.tenant(id), queryFn: () => get<PlatformTenantDetail>(`/platform/tenants/${id}`) });

export const useMembers = (id: string, q: string, enabled: boolean) =>
  useQuery({
    queryKey: pk.members(id, q),
    queryFn: () => get<PlatformMember[]>(`/platform/tenants/${id}/members${qs({ q: q.trim().length >= 2 ? q.trim() : undefined })}`),
    enabled,
    placeholderData: (prev) => prev,
  });

export const usePeople = (q: string) =>
  useQuery({ queryKey: pk.people(q), queryFn: () => get<PlatformPersonResult>(`/platform/people${qs({ q })}`), enabled: q.length >= 3, staleTime: 0 });

export const useJobs = (status: string) => useQuery({ queryKey: pk.jobs(status), queryFn: () => get<PlatformJobs>(`/platform/jobs${qs({ status })}`) });

export const useAdmins = () => useQuery({ queryKey: pk.admins, queryFn: () => get<PlatformAdminRow[]>("/platform/admins") });

export const fetchAudit = (action: string, cursor?: string) => get<PlatformAuditPage>(`/platform/audit${qs({ action: action || undefined, cursor, limit: 50 })}`);

export type { PlatformSupportSession };
